import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Real-time sync: a WebSocket to /api/ws; every change event invalidates the
 * relevant query keys, so open boards, Gantts, timesheets and dashboards
 * refresh without a reload. Reconnects with backoff; silent when the server
 * has realtime disabled.
 */
const KEYS: Record<string, string[][]> = {
  tasks: [['tasks'], ['task'], ['schedule'], ['project-dashboard'], ['analytics'], ['dashboard'], ['search'], ['portfolio-tasks'], ['portfolio-schedule'], ['portfolio-overview']],
  time_entries: [['time'], ['approvals'], ['dashboard'], ['project-dashboard'], ['budget'], ['report'], ['payroll'], ['tasks']],
  approval_periods: [['approvals'], ['dashboard'], ['payroll'], ['time']],
  notifications: [['notifications']],
  projects: [['projects'], ['project'], ['dashboard'], ['portfolio'], ['portfolios']],
  project_members: [['project'], ['projects'], ['users'], ['portfolio']],
  task_dependencies: [['deps'], ['schedule'], ['portfolio-deps'], ['portfolio-schedule']],
  milestones: [['project'], ['project-dashboard'], ['portfolio']],
  portfolios: [['portfolios'], ['portfolio']],
  portfolio_members: [['portfolio'], ['portfolios']],
  task_links: [['task-links']],
  comments: [['comments'], ['notifications']],
  attachments: [['attachments']],
  timers: [['timer']],
}

export function useRealtime(enabled: boolean) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    let ws: WebSocket | null = null, closed = false, delay = 1000, timer: ReturnType<typeof setTimeout> | null = null
    const pending = new Set<string>()
    let flush: ReturnType<typeof setTimeout> | null = null
    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws'
      ws = new WebSocket(`${proto}://${location.host}/api/ws`)
      ws.onopen = () => { delay = 1000 }
      ws.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data as string) as { type: string; table?: string }
          if (ev.type !== 'change' || !ev.table) return
          pending.add(ev.table)
          // Coalesce bursts (a bulk edit fires many events) into one invalidation pass.
          if (!flush) flush = setTimeout(() => { for (const t of pending) for (const k of KEYS[t] ?? []) void qc.invalidateQueries({ queryKey: k }); pending.clear(); flush = null }, 250)
        } catch { /* ignore */ }
      }
      ws.onclose = (e) => { if (closed || e.code === 4401) return; timer = setTimeout(connect, delay); delay = Math.min(delay * 2, 30_000) }
      ws.onerror = () => ws?.close()
    }
    connect()
    return () => { closed = true; if (timer) clearTimeout(timer); if (flush) clearTimeout(flush); ws?.close() }
  }, [enabled, qc])
}
